import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import The190Admin from "@/components/admin/The190Admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nf_admin_token";

const exports_ = [
  {
    key: "waitlist",
    title: "Print Release Waitlist",
    note: "Everyone who used 'Register your interest' or chose the Acquisition topic on the contact form.",
    filename: "nick-fraser-waitlist.csv",
  },
  {
    key: "newsletter",
    title: "Newsletter Subscribers",
    note: "Email addresses captured from the footer newsletter.",
    filename: "nick-fraser-newsletter.csv",
  },
  {
    key: "messages",
    title: "All Contact Messages",
    note: "Every contact form submission across all topics.",
    filename: "nick-fraser-contact-messages.csv",
  },
  {
    key: "inquiries",
    title: "Private Collector Inquiries",
    note: "Enquiries submitted through the password-gated collector area.",
    filename: "nick-fraser-collector-inquiries.csv",
  },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  const loadSummary = async (tok) => {
    try {
      const res = await axios.get(`${API}/admin/summary`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setSummary(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        setToken("");
        toast("Session expired. Please sign in again.");
      } else {
        toast("Could not load summary.");
      }
    }
  };

  useEffect(() => {
    if (token) loadSummary(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setChecking(true);
    try {
      const res = await axios.post(`${API}/admin/verify`, {
        password: password.trim(),
      });
      if (res.data?.ok) {
        localStorage.setItem(STORAGE_KEY, password.trim());
        setToken(password.trim());
        setPassword("");
      } else {
        toast("That is not the key.");
      }
    } catch {
      toast("Something went wrong. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setSummary(null);
  };

  const handleDownload = async (item) => {
    setDownloading(item.key);
    try {
      const res = await axios.get(`${API}/admin/export/${item.key}`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast("Session expired. Please sign in again.");
        handleSignOut();
      } else {
        toast("Download failed. Please try again.");
      }
    } finally {
      setDownloading(null);
    }
  };

  // Gate
  if (!token) {
    return (
      <main
        data-testid="admin-gate"
        className="bone-section min-h-[100svh] flex items-center justify-center px-6 md:px-12"
      >
        <div className="max-w-md w-full">
          <p className="hairline opacity-60 mb-10 text-center">
            Estate · Administration
          </p>
          <h1 className="font-serif italic text-4xl md:text-5xl leading-tight text-center">
            The Administrator's Room.
          </h1>
          <p className="font-serif italic text-lg md:text-xl opacity-70 mt-6 text-center">
            Sign in to access waitlists and exports.
          </p>

          <form onSubmit={handleSignIn} className="mt-16">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="invisible-input text-center"
              placeholder="Key"
              data-testid="admin-password-input"
              autoFocus
            />
            <div className="mt-10 flex items-center justify-between">
              <Link
                to="/"
                className="hairline opacity-50 hover:opacity-90 transition-opacity duration-700"
                data-testid="admin-back-link"
              >
                ← Return
              </Link>
              <button
                type="submit"
                disabled={checking}
                className="link-quiet font-serif italic text-lg disabled:opacity-40"
                data-testid="admin-submit-button"
              >
                {checking ? "…" : "Sign in"}
              </button>
            </div>
          </form>
        </div>
      </main>
    );
  }

  // Authenticated
  return (
    <main
      data-testid="admin-area"
      className="bone-section min-h-[100svh]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-32 md:pb-48">
        <div className="flex items-center justify-between mb-16">
          <p className="hairline opacity-60" data-testid="admin-welcome">
            Estate · Administration
          </p>
          <button
            type="button"
            onClick={handleSignOut}
            className="hairline opacity-50 hover:opacity-90 transition-opacity duration-700"
            data-testid="admin-signout-button"
          >
            Sign out
          </button>
        </div>

        <div className="max-w-3xl mb-20 md:mb-24">
          <h1 className="font-serif italic text-5xl md:text-6xl leading-[1.05]">
            Lists &amp; exports.
          </h1>
          <p className="font-serif italic text-xl md:text-2xl opacity-75 mt-6">
            Download CSVs of the people in conversation with the estate.
          </p>
        </div>

        {/* Summary */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 mb-20 md:mb-28"
          data-testid="admin-summary"
        >
          {[
            { key: "waitlist", label: "Waitlist" },
            { key: "newsletter", label: "Newsletter" },
            { key: "messages", label: "Messages" },
            { key: "collector_inquiries", label: "Collector inquiries" },
          ].map((s) => (
            <div
              key={s.key}
              className="border-t border-[color:var(--rule)] pt-6"
              data-testid={`admin-summary-${s.key}`}
            >
              <p className="hairline opacity-55">{s.label}</p>
              <p className="font-serif italic text-5xl md:text-6xl leading-none mt-3">
                {summary?.[s.key] ?? "—"}
              </p>
            </div>
          ))}
        </div>

        {/* Export list */}
        <ul className="border-t border-[color:var(--rule)]">
          {exports_.map((item) => (
            <li
              key={item.key}
              className="grid grid-cols-12 gap-6 md:gap-12 py-8 md:py-10 border-b border-[color:var(--rule)] items-baseline"
              data-testid={`admin-export-${item.key}`}
            >
              <div className="col-span-12 md:col-span-7">
                <h2 className="font-serif italic text-2xl md:text-3xl">
                  {item.title}
                </h2>
                <p className="font-serif italic text-base md:text-lg opacity-70 mt-2 leading-relaxed">
                  {item.note}
                </p>
              </div>
              <div className="col-span-6 md:col-span-3 font-mono text-xs tracking-widest opacity-50">
                {item.filename}
              </div>
              <div className="col-span-6 md:col-span-2 md:text-right">
                <button
                  type="button"
                  onClick={() => handleDownload(item)}
                  disabled={downloading === item.key}
                  className="link-quiet font-serif italic text-lg md:text-xl disabled:opacity-40"
                  data-testid={`admin-download-${item.key}`}
                >
                  {downloading === item.key ? "Preparing…" : "Download CSV"}
                </button>
              </div>
            </li>
          ))}
        </ul>

        <p className="hairline opacity-40 mt-20 md:mt-28 leading-relaxed max-w-2xl">
          CSVs are generated on the fly from the live database. Open in any
          spreadsheet program. Keep these files private — they contain personal
          contact details.
        </p>

        <The190Admin token={token} />
      </div>
    </main>
  );
}
